import React, { useCallback, useMemo } from 'react';

import { useIntl } from 'react-intl';

import { CheckCircleOutlined, MinusCircleOutlined } from '@ant-design/icons';
import { Button, Tag, Tooltip } from 'antd';

import EventTypeIcon from '@src/components/EventTypeIcon';
import {
  ResponsiveTable,
  type ResponsiveTableColumn,
} from '@src/components/Table';
import { DateUtil } from '@src/packages/utils/dates';

import styles from './styles.module.less';

export interface TriggerTableRow {
  id: string;
  name: string;
  triggerId?: string;
  expectedExecutions?: number;
  finishedExecutions?: number;
  avoidConsecutiveTriggering?: boolean;
  cooldown?: number;
  eventType?: { name?: string; icon?: string; color?: { r: number; g: number; b: number; a?: number } };
}

interface ScheduleTriggersTableProps {
  loading?: boolean;
  onSelectTrigger: (trigger: TriggerTableRow) => void;
  triggers: TriggerTableRow[];
}

const COLUMN_WIDTHS = {
  cooldown: { width: 168 },
  waitTime: { width: 136 },
  status: { width: 132 },
  executions: { width: 104 },
} as const;

const ScheduleTriggersTable: React.FC<ScheduleTriggersTableProps> = ({
  loading = false,
  onSelectTrigger,
  triggers,
}) => {
  const intl = useIntl();
  const avoidConsecutiveLabel = intl.formatMessage({
    id: 'schedule.trigger.avoidConsecutiveTriggering.label',
  });

  const getTriggerStatus = useCallback(
    (trigger: TriggerTableRow) => {
      const finishedExecutions = trigger.finishedExecutions ?? 0;
      const expectedExecutions = trigger.expectedExecutions ?? 0;
      const percentage =
        expectedExecutions > 0 ? (finishedExecutions / expectedExecutions) * 100 : 0;

      return percentage >= 100
        ? {
            label: intl.formatMessage({ id: 'dashboards.schedules.triggerProgress.done' }),
            color: 'success' as const,
          }
        : {
            label: intl.formatMessage({
              id: 'dashboards.schedules.triggerProgress.inProgress',
            }),
            color: 'processing' as const,
          };
    },
    [intl],
  );

  const columns = useMemo<ResponsiveTableColumn<TriggerTableRow>[]>(
    () => [
      {
        key: 'event',
        title: intl.formatMessage({ id: 'timeline.event' }),
        dataIndex: 'name',
        align: 'left',
        render: trigger => (
          <div className={styles.event}>
            {trigger.eventType && (
              <Tooltip title={trigger.eventType.name}>
                <span className={styles.eventIcon}>
                  <EventTypeIcon
                    color={trigger.eventType.color}
                    icon={trigger.eventType.icon}
                  />
                </span>
              </Tooltip>
            )}
            <span className={styles.eventName}>{trigger.name}</span>
          </div>
        ),
      },
      {
        key: 'cooldown',
        title: intl.formatMessage({ id: 'common.cooldown' }),
        dataIndex: 'avoidConsecutiveTriggering',
        align: 'center',
        contentAlign: 'center',
        styles: COLUMN_WIDTHS.cooldown,
        render: trigger =>
          trigger.avoidConsecutiveTriggering ? (
            <Tooltip title={avoidConsecutiveLabel}>
              <CheckCircleOutlined className={styles.cooldownEnabled} />
            </Tooltip>
          ) : (
            <MinusCircleOutlined className={styles.cooldownDisabled} />
          ),
      },
      {
        key: 'waitTime',
        title: intl.formatMessage({ id: 'schedule.trigger.cooldown.label' }),
        dataIndex: 'cooldown',
        align: 'center',
        contentAlign: 'center',
        styles: COLUMN_WIDTHS.waitTime,
        render: trigger => (
          <span className={styles.waitTime}>
            {trigger.cooldown ? DateUtil.formatNumberToMinutes(trigger.cooldown) : '-'}
          </span>
        ),
      },
      {
        key: 'status',
        title: intl.formatMessage({ id: 'general.status' }),
        dataIndex: 'status',
        align: 'center',
        contentAlign: 'center',
        styles: COLUMN_WIDTHS.status,
        render: trigger => {
          const status = getTriggerStatus(trigger);
          return <Tag color={status.color}>{status.label}</Tag>;
        },
      },
      {
        key: 'executions',
        title: intl.formatMessage({ id: 'general.executions' }),
        dataIndex: 'executions',
        align: 'center',
        contentAlign: 'center',
        styles: COLUMN_WIDTHS.executions,
        render: trigger => {
          const finishedExecutions = trigger.finishedExecutions ?? 0;
          const expectedExecutions = trigger.expectedExecutions ?? 0;
          const label = `${finishedExecutions}/${expectedExecutions}`;

          if (finishedExecutions === 0) {
            return <span className={styles.executionsIdle}>{label}</span>;
          }

          return (
            <Button
              type="link"
              className={styles.executionsButton}
              onClick={() => onSelectTrigger(trigger)}
            >
              <span className={styles.executions}>{label}</span>
            </Button>
          );
        },
      },
    ],
    [avoidConsecutiveLabel, getTriggerStatus, intl, onSelectTrigger],
  );

  return (
    <ResponsiveTable
      type="light"
      dataSource={triggers}
      columns={columns}
      loading={loading}
      className={styles.table}
      wrapperClassName={styles.tableWrapper}
    />
  );
};

export default React.memo(ScheduleTriggersTable);
